/* eslint-disable class-methods-use-this */
import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { MAX_SUBSCRIBED_CONVERSATIONS } from '../store/modules/conversations/helpers';
import { createRafBatcher } from './rafBatcher';

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];

    // Coalesce the high-volume conversation-list mutations (new messages,
    // created/updated/assigned conversations) into one flush per animation
    // frame. Under a campaign burst (~120+ events/s) each event otherwise lands
    // in its own tick and forces a separate list re-filter/re-sort/re-render;
    // draining them together collapses a frame's worth of events into a single
    // render. All of them share ONE queue so their relative order is preserved.
    this.listUpdateBatcher = createRafBatcher();

    this.events = {
      'custom_filter.created': this.onCustomFilterCreated,
      'custom_filter.updated': this.onCustomFilterUpdated,
      'custom_filter.deleted': this.onCustomFilterDeleted,
      'automation.created': this.onAutomationCreated,
      'automation.updated': this.onAutomationUpdated,
      'automation.deleted': this.onAutomationDeleted,
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'bulk.assignee.changed': this.onBulkAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'native_attribute_requirement.created':
        this.onNativeAttributeRequirementCreated,
      'native_attribute_requirement.deleted':
        this.onNativeAttributeRequirementDeleted,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'first.reply.created': this.onFirstReplyCreated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'conversation.merged': this.onConversationMerged,
      'account.cache_invalidated': this.onCacheInvalidate,
      'claudia.takeover_batch.progress': this.onClaudiaTakeoverBatchProgress,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    const normalize = id => (id == null ? null : parseInt(id, 10));
    const currentAccountId = normalize(
      this.app.$store.getters.getCurrentAccountId
    );
    const eventAccountId = normalize(
      data?.account_id ||
        data?.notification?.account_id ||
        data?.conversation?.account_id ||
        data?.message?.account_id ||
        data?.custom_filter?.account_id ||
        data?.customFilter?.account_id
    );
    return currentAccountId !== null && currentAccountId === eventAccountId;
  };

  onMessageUpdated = data => {
    // Same queue as onMessageCreated: both resolve to the ADD_MESSAGE upsert, so
    // an immediate update racing a batched create would apply first and then be
    // reverted when the older create flushes. Sharing the queue keeps order.
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch('updateMessage', data);
    });
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    // Same queue as onConversationCreated: this touches an existing list row
    // (UPDATE_CONVERSATION_CONTACT no-ops when the row is absent), so running it
    // before an earlier batched create for the same conversation would drop the
    // sender update. Sharing the queue keeps WebSocket order.
    this.listUpdateBatcher.enqueue(() => {
      const { meta = {}, id: conversationId } = payload;
      const { sender } = meta || {};
      if (conversationId) {
        this.app.$store.dispatch('updateConversationContact', {
          conversationId,
          ...sender,
        });
      }
    });
  };

  onAssigneeChanged = payload => {
    // Audio at receive time (see onMessageCreated); list mutation batched.
    DashboardAudioNotificationHelper.onAssigneeChanged(payload);
    this.listUpdateBatcher.enqueue(() => this.applyAssigneeChanged(payload));
  };

  // Extracted from the batched closure so the enqueued callback stays a
  // one-liner; guard clauses keep the nesting shallow.
  applyAssigneeChanged = payload => {
    const { id, meta } = payload;
    if (!id) return;
    this.app.$store.dispatch('updateConversation', payload);

    // When the current user is viewing this conversation and is no longer the
    // assignee, subscribe to ConversationChannel so they keep receiving live
    // message updates (non-assignees only get messages via that channel).
    const currentConversationId = this.app.$store.getters.getConversationId;
    if (currentConversationId !== id) return;
    const currentUserId = this.app.$store.getters.getCurrentUserID;
    const newAssigneeId = meta?.assignee?.id;
    if (newAssigneeId && newAssigneeId === currentUserId) return;
    const pubsubToken = this.app.$store.getters.getCurrentUserPubsubToken;
    if (!pubsubToken) return;
    const subs =
      this.app.$store.state.conversations.subscribedNonAssignedConversations;
    const conversationOldestId =
      subs.length >= MAX_SUBSCRIBED_CONVERSATIONS ? subs[0] : null;
    this.app.$store.dispatch('subscribeToConversation', {
      conversationId: id,
      pubsubToken,
      conversationOldestId,
    });
  };

  onBulkAssigneeChanged = payload => {
    const conversations = payload.conversations || [];
    // Audio at receive time (see onMessageCreated); list mutations batched.
    conversations.forEach(conversation =>
      DashboardAudioNotificationHelper.onAssigneeChanged(conversation)
    );
    this.listUpdateBatcher.enqueue(() => {
      conversations.forEach(conversation =>
        this.app.$store.dispatch('updateConversation', conversation)
      );
    });
  };

  onConversationCreated = data => {
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch('addConversation', data);
    });
  };

  onConversationRead = data => {
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch('updateConversation', data);
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt } = {},
      conversation_id: conversationId,
    } = data;

    const payload = {
      ...data,
      conversation_id: parseInt(conversationId, 10),
    };

    // The audio alert decides whether to play from document.hidden at call
    // time. It must run at receive time, NOT inside the batcher: a flush can
    // land after the tab is refocused, which would suppress a hidden-tab alert.
    // Only the list mutations are batched.
    DashboardAudioNotificationHelper.onNewMessage(payload);
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch('addMessage', payload);
      if (lastActivityAt) {
        this.app.$store.dispatch('updateConversationLastActivity', {
          lastActivityAt,
          conversationId: payload.conversation_id,
        });
      }
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onAutomationCreated = payload => {
    emitter.emit('automation_created', payload);
  };

  onCustomFilterCreated = payload => {
    if (!this.isAValidEvent(payload)) return;
    const customFilter =
      payload.custom_filter || payload.customFilter || payload;
    // Namespaced commit to customViews module
    this.app.$store.commit('customViews/SET_SINGLE_CUSTOM_VIEW', customFilter);
  };

  onCustomFilterUpdated = payload => {
    if (!this.isAValidEvent(payload)) return;
    const customFilter =
      payload.custom_filter || payload.customFilter || payload;
    this.app.$store.commit('customViews/SET_SINGLE_CUSTOM_VIEW', customFilter);
  };

  onCustomFilterDeleted = payload => {
    if (!this.isAValidEvent(payload)) return;
    const id =
      payload.custom_filter?.id || payload.customFilter?.id || payload.id;
    if (id) this.app.$store.commit('customViews/DELETE_CUSTOM_VIEW', id);
  };

  onAutomationUpdated = payload => {
    emitter.emit('automation_updated', payload);
  };

  onAutomationDeleted = payload => {
    emitter.emit('automation_deleted', payload);
  };

  onClaudiaTakeoverBatchProgress = payload => {
    emitter.emit('claudia.takeover_batch.progress', payload);
  };

  onNativeAttributeRequirementCreated = payload => {
    emitter.emit('native_attribute_requirement.created', payload);
  };

  onNativeAttributeRequirementDeleted = payload => {
    emitter.emit('native_attribute_requirement.deleted', payload);
  };

  onStatusChange = data => {
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch('updateConversation', data);
    });
  };

  onConversationUpdated = data => {
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch('updateConversation', data);
    });
  };

  // F9 — real-time cross-agent sync for conversation merges. `data` is the
  // survivor's own push_event_data (so `data.id` IS the survivor's display_id
  // already) plus `source_display_ids`, the display_ids of the losers merged
  // into it (see ConversationMergeAction#dispatch_merged_event +
  // ActionCableListener#conversation_merged). The initiating agent does NOT
  // need this: their own mergeConversations action response already drove
  // the MERGE_CONVERSATIONS mutation locally. This handler is for every
  // OTHER agent who may have a loser open or listed.
  //
  // We deliberately do NOT refetch the survivor here: its new merged-history
  // activity pill arrives via the normal message.created broadcast (F5) —
  // refetching on top of that would be redundant work on every merge.
  onConversationMerged = data => {
    // Shares listUpdateBatcher with the other list mutations so a merge cannot
    // be reordered relative to a batched create/update for the same rows.
    this.listUpdateBatcher.enqueue(() => {
      const { id: survivorId, source_display_ids: sourceIds = [] } = data;
      if (!sourceIds.length) return;

      // The merge is always same-contact (ConversationMergeAction validates all
      // participants share one contact_id), so the survivor's own embedded
      // `meta.sender` (see Conversations::EventDataPresenter#push_meta) IS the
      // contact — used to also patch that contact's cached conversation list
      // (contactConversations), so its "Previous Conversations" panel/Merged
      // badge stays live rather than stale until refetch.
      const contactId = data?.meta?.sender?.id;

      this.app.$store.dispatch('conversationMergedRealtime', {
        sourceIds,
        survivorId,
        contactId,
      });
    });
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;
    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;
    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    // Same queue as onConversationCreated: on the mentions view addMentions
    // upserts the row via updateConversation, so it must not run before an
    // earlier batched create for the same conversation. Sharing the queue keeps
    // WebSocket order.
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch('addMentions', data);
    });
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];
    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 5000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
    emitter.emit('fetch_overview_reports');
  };

  onContactDelete = data => {
    // Same queue as the conversation handlers: deleteContactThroughConversations
    // clears the contact's rows (CLEAR_CONTACT_CONVERSATIONS) and drops the
    // contact, while batched createConversation/updateConversationContact write
    // the same list and contacts store. Running this immediately lets a later
    // batched flush resurrect the deleted contact/chat; sharing the queue keeps
    // WebSocket order.
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch(
        'contacts/deleteContactThroughConversations',
        data.id
      );
    });
  };

  onContactUpdate = data => {
    // Same queue as onConversationContactChange: both write the contacts store
    // (EDIT_CONTACT vs SET_CONTACT_ITEM). Running this immediately lets a later
    // batched contact_changed flush overwrite the newer fields with a stale
    // sender snapshot; sharing the queue keeps WebSocket order.
    this.listUpdateBatcher.enqueue(() => {
      this.app.$store.dispatch('contacts/updateContact', data);
    });
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onFirstReplyCreated = () => {
    emitter.emit('fetch_overview_reports');
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  connectorInstance: null,
  init(pubsubToken) {
    this.connectorInstance = new ActionCableConnector(window.WOOT, pubsubToken);
    return this.connectorInstance;
  },
  getConnector() {
    return this.connectorInstance;
  },
};
